import { useState, useEffect } from 'react';
import {
  Loader2, Webhook, RefreshCw, Trash2, CheckCircle, AlertTriangle,
  Clock, XCircle, Settings2, Play, Zap, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: { color: 'bg-amber-50 text-amber-600', dot: 'bg-amber-400', icon: Clock },
  delivered: { color: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500', icon: CheckCircle },
  failed: { color: 'bg-red-50 text-red-600', dot: 'bg-red-500', icon: XCircle },
};

const NIAIntegrationTab = ({ headers }) => {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, delivered: 0, failed: 0, total: 0 });
  const [stats, setStats] = useState(null);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showConfig, setShowConfig] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [flushResult, setFlushResult] = useState(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchData = async () => {
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const [evtRes, cfgRes, statsRes] = await Promise.all([
        fetch(`${API}/api/nia/events${statusParam}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/nia/webhook/config`, { headers, credentials: 'include' }),
        fetch(`${API}/api/nia/stats`, { headers, credentials: 'include' }),
      ]);
      if (evtRes.ok) {
        const data = await evtRes.json();
        setEvents(data.events || []);
        setCounts(data.counts || { pending: 0, delivered: 0, failed: 0, total: 0 });
      }
      if (cfgRes.ok) setWebhookConfig(await cfgRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error('Failed to fetch NIA data:', e); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount + filter change
  useEffect(() => { fetchData(); }, [filter]);

  const updateWebhook = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/nia/webhook/config`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        setWebhookConfig(prev => ({ ...prev, ...data }));
      }
    } catch (e) { console.error('Failed to update webhook config:', e); }
    finally { setSaving(false); }
  };

  const retryEvent = async (eventId) => {
    await fetch(`${API}/api/nia/events/${eventId}/retry`, { method: 'POST', headers, credentials: 'include' });
    fetchData();
  };

  const deleteEvent = async (eventId) => {
    await fetch(`${API}/api/nia/events/${eventId}`, { method: 'DELETE', headers, credentials: 'include' });
    fetchData();
  };

  const flushEvents = async () => {
    setFlushing(true);
    setFlushResult(null);
    try {
      const res = await fetch(`${API}/api/nia/webhook/flush`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFlushResult(data);
        fetchData();
      }
    } catch (e) { console.error('Failed to flush events:', e); }
    finally { setFlushing(false); }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/api/nia/webhook/test`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) setTestResult(await res.json());
    } catch (e) {
      setTestResult({ success: false, error: 'Connection error' });
    }
    finally { setTestingWebhook(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-nia">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'Total Events', value: counts.total, color: '#0066CC' },
          { label: 'Pending', value: counts.pending, color: '#f59e0b' },
          { label: 'Delivered', value: counts.delivered, color: '#10b981' },
          { label: 'Failed', value: counts.failed, color: '#ef4444' },
          { label: 'Last 24h', value: stats?.last_24h || 0, color: '#8b5cf6' },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`nia-stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">{card.label}</span>
            <div className="font-heading text-2xl font-bold mt-1" style={{ color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Event Type Breakdown */}
      {stats?.by_type && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-taxera-charcoal mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-400" /> Events by Type
          </h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.by_type).filter(([, v]) => v > 0).map(([type, count]) => (
              <div key={type} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-600 capitalize">{type.replace('_', ' ')}</span>
                <span className="text-xs font-bold text-taxera-charcoal">{count}</span>
              </div>
            ))}
            {Object.values(stats.by_type).every(v => v === 0) && (
              <p className="text-xs text-slate-400">No events yet</p>
            )}
          </div>
        </div>
      )}

      {/* Webhook Configuration */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <button onClick={() => setShowConfig(!showConfig)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          data-testid="nia-webhook-toggle">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
              <Webhook className="w-4 h-4 text-violet-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-bold text-taxera-charcoal">Webhook Configuration</h3>
              <p className="text-2xs text-slate-400">
                {webhookConfig?.enabled ? 'Active — events auto-flushed every 5 min' : 'Inactive — events queued but not delivered'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${webhookConfig?.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <Settings2 className={`w-4 h-4 text-slate-400 transition-transform ${showConfig ? 'rotate-90' : ''}`} />
          </div>
        </button>

        {showConfig && webhookConfig && (
          <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={() => updateWebhook({ enabled: !webhookConfig.enabled })}
                className={`w-10 h-6 rounded-full transition-colors relative ${webhookConfig.enabled ? 'bg-taxera-teal' : 'bg-slate-300'}`}
                data-testid="nia-webhook-enabled">
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${webhookConfig.enabled ? 'left-5' : 'left-1'}`} />
              </button>
              <span className="text-sm font-medium text-slate-600">Enable webhook delivery</span>
            </div>
            <div>
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Endpoint URL</label>
              <input type="url" value={webhookConfig.endpoint_url || ''}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, endpoint_url: e.target.value }))}
                onBlur={(e) => updateWebhook({ endpoint_url: e.target.value })}
                placeholder="https://your-nia-instance.com/webhook"
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal font-mono"
                data-testid="nia-endpoint-url" />
            </div>
            <div>
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Authorization Header</label>
              <input type="password" value={webhookConfig.auth_header || ''}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, auth_header: e.target.value }))}
                onBlur={(e) => updateWebhook({ auth_header: e.target.value })}
                placeholder="Bearer sk-..."
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal font-mono"
                data-testid="nia-auth-header" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Max Retries</label>
                <input type="number" value={webhookConfig.retry_max || 3} min={1} max={10}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 3;
                    setWebhookConfig(prev => ({ ...prev, retry_max: val }));
                    updateWebhook({ retry_max: val });
                  }}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="nia-retry-max" />
              </div>
              <div>
                <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Retry Delay (sec)</label>
                <input type="number" value={webhookConfig.retry_delay_seconds || 60} min={10} max={3600}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 60;
                    setWebhookConfig(prev => ({ ...prev, retry_delay_seconds: val }));
                    updateWebhook({ retry_delay_seconds: val });
                  }}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="nia-retry-delay" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <button onClick={testWebhook} disabled={testingWebhook || !webhookConfig.endpoint_url}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50"
                data-testid="nia-test-webhook">
                {testingWebhook ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Test Webhook
              </button>
              <button onClick={flushEvents} disabled={flushing || !webhookConfig.enabled || counts.pending === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-taxera-teal text-white rounded-lg text-xs font-semibold hover:shadow-md transition-all disabled:opacity-50"
                data-testid="nia-flush-btn">
                {flushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Flush Pending ({counts.pending})
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-3 rounded-lg text-xs flex items-start gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}
                data-testid="nia-test-result">
                {testResult.success ? <CheckCircle className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                <div>
                  <span className="font-semibold">{testResult.success ? 'Webhook reachable' : 'Webhook unreachable'}</span>
                  {testResult.status_code && <span className="ml-1 text-emerald-500">(HTTP {testResult.status_code})</span>}
                  {testResult.error && <p className="mt-1 text-2xs">{testResult.error}</p>}
                </div>
              </div>
            )}

            {/* Flush Result */}
            {flushResult && (
              <div className="p-3 rounded-lg text-xs bg-blue-50 text-blue-700 flex items-center gap-2" data-testid="nia-flush-result">
                <CheckCircle className="w-4 h-4 shrink-0" />
                <span>{flushResult.message} — Delivered: {flushResult.delivered}, Failed: {flushResult.failed}</span>
              </div>
            )}

            {saving && <p className="text-2xs text-taxera-teal font-medium">Saving...</p>}
          </div>
        )}
      </div>

      {/* Event Queue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-taxera-charcoal">Event Queue</h3>
          <div className="flex items-center gap-2">
            {['all', 'pending', 'delivered', 'failed'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`text-2xs px-3 py-1.5 rounded-full font-semibold transition-colors ${
                  filter === f ? 'bg-taxera-charcoal text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`nia-filter-${f}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
            <Webhook className="w-8 h-8 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No events in the queue.</p>
            <p className="text-xs text-slate-300 mt-1">Events appear as leads, demos, and forms are submitted.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {events.map(evt => {
              const style = STATUS_STYLES[evt.status] || STATUS_STYLES.pending;
              const StatusIcon = style.icon;
              return (
                <div key={evt.event_id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3" data-testid={`nia-event-${evt.event_id}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${style.color}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-bold text-taxera-charcoal capitalize">{evt.event_type?.replace('_', ' ')}</span>
                      <span className={`text-2xs px-1.5 py-0.5 rounded-full font-semibold ${style.color}`}>{evt.status}</span>
                      <span className="text-2xs text-slate-400">{evt.source}</span>
                    </div>
                    <div className="text-2xs text-slate-400">
                      {evt.event_id} — {new Date(evt.created_at).toLocaleString()}
                      {evt.retries > 0 && <span className="ml-2 text-amber-500">({evt.retries} retries)</span>}
                      {evt.delivered_at && <span className="ml-2 text-emerald-500">Delivered {new Date(evt.delivered_at).toLocaleString()}</span>}
                    </div>
                    {evt.error && (
                      <div className="mt-1 text-2xs text-red-400 bg-red-50 rounded px-2 py-1 truncate">{evt.error}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {evt.status === 'failed' && (
                      <button onClick={() => retryEvent(evt.event_id)}
                        className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors" title="Retry"
                        data-testid={`nia-retry-${evt.event_id}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => deleteEvent(evt.event_id)}
                      className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors" title="Delete"
                      data-testid={`nia-delete-${evt.event_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NIAIntegrationTab;
